import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import FloatingCallCard from '../components/FloatingCallCard';
import { useLanguage } from '../context/LanguageContext';
import { useTransition } from '../context/TransitionContext';

gsap.registerPlugin(ScrollTrigger);

const HomeDeliverables = () => {
    const containerRef = useRef(null);
    const cardsRef = useRef([]);
    const { startTransition } = useTransition();
    const { t } = useLanguage();
    const [cardVisible, setCardVisible] = useState(true);

    // Hide card when the next section ("Featured work") enters the viewport
    useEffect(() => {
        const nextSection = document.getElementById('featured-work');
        if (!nextSection) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                setCardVisible(!entry.isIntersecting);
            },
            { threshold: 0.1 }
        );
        observer.observe(nextSection);
        return () => observer.disconnect();
    }, []);

    useLayoutEffect(() => {
        const ctx = gsap.context(() => {
            const cards = cardsRef.current;
            const totalCards = cards.length;
            const segmentSize = 1 / totalCards;
            const cardYOffset = -6; // Negative offset to peek from the top
            const cardScaleStep = 0.05; // Slightly less scale down so they don't shrink too fast

            // Initial setup
            cards.forEach((card, i) => {
                if (card) {
                    gsap.set(card, {
                        xPercent: -50,
                        yPercent: -50 + i * cardYOffset,
                        scale: 1 - i * cardScaleStep,
                        rotationX: 0,
                        opacity: 1
                    });
                }
            });

            ScrollTrigger.create({
                trigger: containerRef.current,
                start: "top top",
                end: () => `+=${window.innerHeight * 4}px`,
                pin: true,
                pinSpacing: true,
                scrub: 1,
                anticipatePin: 1,
                onUpdate: (self) => {
                    const progress = self.progress;
                    const activeIndex = Math.min(
                        Math.floor(progress / segmentSize),
                        totalCards - 1,
                    );
                    const segProgress = (progress - activeIndex * segmentSize) / segmentSize;

                    cards.forEach((card, i) => {
                        if (!card) return;
                        if (i < activeIndex) {
                            gsap.set(card, {
                                xPercent: -50,
                                yPercent: -250,
                                rotationX: 35,
                            });
                        } else if (i === activeIndex) {
                            gsap.set(card, {
                                xPercent: -50,
                                yPercent: gsap.utils.interpolate(-50, -200, segProgress),
                                rotationX: gsap.utils.interpolate(0, 35, segProgress),
                                scale: 1,
                            });
                        } else {
                            const behindIndex = i - activeIndex;
                            const currentOffset = (behindIndex - segProgress) * cardYOffset;
                            const currentScale = 1 - (behindIndex - segProgress) * cardScaleStep;

                            gsap.set(card, {
                                xPercent: -50,
                                yPercent: -50 + currentOffset,
                                rotationX: 0,
                                scale: currentScale,
                            });
                        }
                    });
                },
            });
        }, containerRef);

        return () => ctx.revert();
    }, []);

    const deliverables = [
        { title: "Branding that drives conversion & funding.", number: "(01)", bgColor: "#3D2FA9" },
        { title: "Interfaces built for pure engagement.", number: "(02)", bgColor: "#FF7722" },
        { title: "Design systems to scale gracefully.", number: "(03)", bgColor: "#FF3C34" },
        { title: "Copywriting that sounds like you.", number: "(04)", bgColor: "#785F47" },
    ];

    return (
        <>
            <section className="v2-container" style={{ padding: '120px 24px', textAlign: 'center' }}>
                <h2 className="v2-display-l">
                    <span style={{ color: 'var(--v2-color-taupe)' }}>{t('home.whatWeShip')}</span><br/>
                    {t('home.whatWeShipSubtitle')}
                </h2>
            </section>
            
            <section 
                ref={containerRef} 
                style={{ 
                    position: 'relative', 
                    width: '100%', 
                    height: '100vh', 
                    overflow: 'hidden',
                    perspective: '850px',
                    backgroundColor: 'var(--v2-color-offwhite)'
                }}
            >
                {deliverables.map((item, index) => (
                    <div 
                        key={index}
                        ref={el => cardsRef.current[index] = el}
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: 'calc(50% + 40px)', // Center based on v2-container padding (200px left, 120px right)
                            width: 'calc(100% - 320px)',
                            maxWidth: '1100px',
                            height: '70vh',
                            backgroundColor: item.bgColor,
                            borderRadius: '32px',
                            color: 'white',
                            transformOrigin: 'top center',
                            willChange: 'transform',
                            padding: '64px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            zIndex: deliverables.length - index
                        }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                            <h3 className="v2-display-l" style={{ color: 'white', maxWidth: '600px', textAlign: 'left', margin: 0, letterSpacing: '-0.02em', lineHeight: 0.9 }}>
                                {item.title}
                            </h3>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'rgba(255,255,255,0.6)' }}>
                                {item.number}
                            </div>
                        </div>
                        
                        <div>
                            <button 
                                onClick={() => startTransition('/work')}
                                style={{
                                    backgroundColor: 'white',
                                    color: 'black',
                                    border: 'none',
                                    borderRadius: '24px',
                                    padding: '16px 32px',
                                    fontWeight: 'bold',
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                {t('home.seeCaseStudies')} &rarr;
                            </button>
                        </div>
                    </div>
                ))}

                {/* Floating Call Card */}
                <FloatingCallCard visible={cardVisible} />
            </section>
        </>
    );
};

export default HomeDeliverables;
